import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRestQuery } from 'utils/hooks/api/useRestQuery';
import noop from 'utils/noop';
import { selectActingUser } from 'core/userContext';
import { EmbeddedMessageMedium } from 'constants/messageMediums';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useListById } from '../EditEmbedded/hooks/queries';

const GET_DISPLAY_TEST_MESSAGE_LIST_ID_PATH = '/embedded-messaging/proofs';
const GET_DISPLAY_TEST_MESSAGE_ADDRESSES_PATH = '/embedded-messaging/proofs/addresses';
const GET_DISPLAY_TEST_MESSAGE_SELF_PATH = '/embedded-messaging/proofs/self';

type DisplayTestMessageStatusResponse = {
  internalListIdDisplaying: number | undefined;
  internalListSizeDisplaying: number | undefined;
  internalListDetailsName: string;
  isDisplayingForSelf: boolean;
  otherAddressesDisplaying: string[];
  totalSizeDisplaying: number | undefined;
  refetch: () => void;
};

export const useDisplayTestMessageStatus = (): DisplayTestMessageStatusResponse => {
  const { liveTemplate, selectedMessageMedium } = useEditableFieldsContext();
  const actingUser = useSelector(selectActingUser);

  // Never auto refetch with hook since we manually refetch in the test message handler in SendTestMsgContextProvider
  const queryConfig = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: selectedMessageMedium === EmbeddedMessageMedium.Embedded,
    retry: true,
  };

  const { data: isSelfDisplaying, refetch: refetchSelf } = useRestQuery(
    `${GET_DISPLAY_TEST_MESSAGE_SELF_PATH}`,
    { templateId: liveTemplate.templateId, email: actingUser },
    queryConfig,
  );
  const { data: listDisplayId, refetch: refetchListId } = useRestQuery(
    `${GET_DISPLAY_TEST_MESSAGE_LIST_ID_PATH}`,
    { templateId: liveTemplate.templateId },
    queryConfig,
  );
  const { data: addressesDisplaying, refetch: refetchAddresses } = useRestQuery(
    `${GET_DISPLAY_TEST_MESSAGE_ADDRESSES_PATH}`,
    { templateId: liveTemplate.templateId },
    queryConfig,
  );

  const handleRefetch = () => {
    refetchListId();
    refetchAddresses();
    refetchSelf();
  };

  const selectedList = useListById(listDisplayId);

  const otherAddressesDisplaying = useMemo(
    () => addressesDisplaying?.filter((address: string) => address !== actingUser) ?? [],
    [addressesDisplaying, actingUser],
  );

  // Short circuit for non embedded templates since we don't want to return info that affects embedded test message display
  if (selectedMessageMedium !== EmbeddedMessageMedium.Embedded) {
    return {
      internalListIdDisplaying: undefined,
      internalListSizeDisplaying: selectedList?.size,
      internalListDetailsName: '',
      isDisplayingForSelf: false,
      otherAddressesDisplaying: [],
      totalSizeDisplaying: 0,
      refetch: noop,
    };
  }

  return {
    internalListIdDisplaying: listDisplayId || null,
    internalListSizeDisplaying: Math.min(selectedList?.size || 0, 50),
    internalListDetailsName: selectedList?.name ?? '',
    isDisplayingForSelf: !!isSelfDisplaying,
    otherAddressesDisplaying,
    totalSizeDisplaying:
      Math.min(otherAddressesDisplaying?.length || 0, 50) +
      Math.min(selectedList?.size || 0, 50) +
      (isSelfDisplaying ? 1 : 0),
    refetch: handleRefetch,
  };
};
